import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

interface ReCaptchaProps {
  onSuccess?: (token: string, version: 'v2' | 'v3') => void;
  onReset?: () => void;
  action?: string;
  minimumScore?: number;
}

interface ReCaptchaHandle {
  executeV3Captcha: () => void;
  isCaptchaVerifying: boolean;
}

const ReCaptcha = React.forwardRef<ReCaptchaHandle, ReCaptchaProps>(({ 
  onSuccess, 
  onReset, 
  action = 'submit',
  minimumScore = 0.5 
}, ref) => {
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [showV2Fallback, setShowV2Fallback] = useState<boolean>(false);
  const [v3Failed, setV3Failed] = useState<boolean>(false);
  const recaptchaV2Ref = useRef<ReCAPTCHA | null>(null);

  const V2_SITE_KEY = process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY;
  const V3_SITE_KEY = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;

  useEffect(() => {
    if (!V3_SITE_KEY) return;

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${V3_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector(`script[src*="${V3_SITE_KEY}"]`);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [V3_SITE_KEY]);

  const executeV3Captcha = async () => {
    if (!V3_SITE_KEY || !window.grecaptcha) {
      fallbackToV2();
      return;
    }

    setIsVerifying(true);
    setVerificationError(null);

    try {
      window.grecaptcha.ready(async () => {
        try {
          const token = await window.grecaptcha.execute(V3_SITE_KEY, { action });
          await verifyV3Token(token);
        } catch (error) {
          fallbackToV2();
        }
      });
    } catch (error) {
      fallbackToV2();
    }
  };
  useImperativeHandle(ref, () => ({
    executeV3Captcha,
    isCaptchaVerifying: isVerifying,
  }));
  const verifyV3Token = async (token: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_ROOT}/recaptcha-v3/verify/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recaptchaToken: token,
          action,
          minimumScore
        }),
      });

      const result = await response.json();

      if (response.ok || result.success) {
        if (result.score >= minimumScore) {
          setRecaptchaToken(token);
          if (onSuccess) {
            onSuccess(token, 'v3');
          }
        } else {
          // Score too low, fallback to v2
          fallbackToV2(`Score too low (${result.score}). Please complete the visual challenge.`);
        }
      } else {
        // v3 verification failed, fallback to v2
        fallbackToV2(result.message || 'Automatic verification failed. Please complete the visual challenge.');
      }
    } catch (error) {
      fallbackToV2('Network error during verification. Please complete the visual challenge.');
    } finally {
      setIsVerifying(false);
    }
  };

  const fallbackToV2 = (errorMessage?: string) => {
    setV3Failed(true);
    setShowV2Fallback(true);
    setIsVerifying(false);
    if (errorMessage) {
      setVerificationError(errorMessage);
    }
  };

  const handleV2RecaptchaChange = async (token: string | null) => {
    setRecaptchaToken(token);
    setVerificationError(null);

    if (!token) {
      if (onReset) {
        onReset();
      }
      return;
    }

    setIsVerifying(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_ROOT}/recaptcha/verify/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recaptchaToken: token
        }),
      });

      const result = await response.json();

      if (response.ok || result.success) {
        if (onSuccess) {
          onSuccess(token, 'v2');
        }
      } else {
        setVerificationError('reCAPTCHA verification failed. Please try again.');
        setRecaptchaToken(null);
        recaptchaV2Ref.current?.reset();
        if (onReset) {
          onReset();
        }
      }
    } catch (error) {
      console.error('reCAPTCHA v2 verification error:', error);
      setVerificationError('Network error during verification. Please try again.');
      setRecaptchaToken(null);
      recaptchaV2Ref.current?.reset();
      if (onReset) {
        onReset();
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleV2RecaptchaExpired = () => {
    setRecaptchaToken(null);
    setVerificationError(null);
    if (onReset) {
      onReset();
    }
  };

  const handleV2RecaptchaError = () => {
    setRecaptchaToken(null);
    setVerificationError('reCAPTCHA error occurred. Please try again.');
    if (onReset) {
      onReset();
    }
  };

  const retryV3 = () => {
    setShowV2Fallback(false);
    setV3Failed(false);
    setVerificationError(null);
    setRecaptchaToken(null);
    executeV3Captcha();
  };

  if (!V2_SITE_KEY || !V3_SITE_KEY) {
    return (
      <div className="error-message p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        <p>reCAPTCHA site keys are missing.</p>
      </div>
    );
  }

  return (
    <div className="recaptcha-container">
      <div className="flex flex-col items-center space-y-3">
        
        {!showV2Fallback && (
          <div className="text-center">
            {isVerifying && (
              <React.Fragment>
                <div className="text-sm text-gray-600 mb-2">
                  Automatic verification in progress...
                </div>
                <div className="flex items-center justify-center space-x-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm">Verifying automatically...</span>
                </div>
              </React.Fragment>
            )}
          </div>
        )}

        {showV2Fallback && (
          <div className="w-full">
            <div className="text-center mb-3">
              <div className="text-sm text-orange-600 mb-2">
                {v3Failed ? 'Additional verification required' : 'Please complete the challenge'}
              </div>
              <ReCAPTCHA
                ref={recaptchaV2Ref}
                sitekey={V2_SITE_KEY}
                onChange={handleV2RecaptchaChange}
                onExpired={handleV2RecaptchaExpired}
                onError={handleV2RecaptchaError}
              />
            </div>
          </div>
        )}
        
        {isVerifying && showV2Fallback && (
          <div className="flex items-center space-x-2 text-blue-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-sm">Verifying...</span>
          </div>
        )}
        
        {verificationError && (
          <div className="text-red-600 text-sm text-center max-w-xs bg-red-50 p-2 rounded border border-red-200">
            {verificationError}
          </div>
        )}
        
        {recaptchaToken && !isVerifying && !verificationError && (
          <div className="text-green-600 text-sm flex items-center space-x-1 bg-green-50 p-2 rounded border border-green-200">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Verified successfully</span>
          </div>
        )}

        {(showV2Fallback && !isVerifying) && (
          <button 
            onClick={retryV3}
            className="text-blue-600 text-sm underline hover:text-blue-800 transition-colors"
          >
            Try automatic verification again
          </button>
        )}
      </div>
    </div>
  );
});

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export default ReCaptcha;